<template>
  <d-modal
    :active="isActive"
    :width="800"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          {{ item.name }}
        </h3>
        <h2 class="text-4xl font-bold">
          Add/Update Questions
        </h2>
      </div>

      <!-- Questions List -->
      <div class="mb-6 max-h-96 overflow-y-auto">
        <div v-if="questions.length === 0" class="text-center py-8 text-gray-500">
          No questions added yet. Click "Add Question" to get started.
        </div>
        
        <div v-else class="space-y-4">
          <div 
            v-for="(question, index) in questions" 
            :key="question.id" 
            class="p-4 bg-white border rounded-lg hover:bg-gray-50 group"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <p class="font-bold text-lg mb-2">
                  #{{ index + 1 }} - {{ question.content }}
                </p>
                <div class="text-sm">
                  <p v-if="question.type == 'Multiple Choice' || question.type == 'Multiple Answer' || question.type == 'Drop Down'">
                    <span class="font-medium">Answer:</span> {{ getCorrectAnswers(question) }}
                  </p>
                  <p v-else-if="question.type == 'File Upload'">
                    <span class="font-medium">Answer:</span> File upload
                  </p>
                  <p v-else-if="question.type == 'Text'">
                    <span class="font-medium">Answer:</span> Free text
                  </p>
                  <p class="mt-1">
                    <span class="font-medium">Type:</span> {{ question.type }}
                  </p>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <d-btn 
                  size="xs" 
                  flat 
                  color="primary"
                  @click="launchQuestionModal(question)"
                >
                  <d-icon icon="edit" />
                </d-btn>
                <d-btn 
                  size="xs" 
                  flat 
                  color="danger"
                  @click="launchDeletePrompt(question)"
                >
                  <d-icon icon="trash" />
                </d-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-between items-center pt-4 border-t">
        <div class="flex gap-2">
          <d-btn 
            @click="launchQuestionModal()"
            color="secondary"
          >
            <d-icon icon="plus" class="mr-1" />
            Add Question
          </d-btn>
          
          <d-btn 
            v-if="questions.length > 0"
            @click="exportQuestions"
            :loading="exporting"
            color="primary"
            flat
          >
            <d-icon icon="download" class="mr-1" />
            Export Questions
          </d-btn>
        </div>
        
        <d-btn 
          flat 
          @click="handleClose"
        >
          Close
        </d-btn>
      </div>
    </div>
  </d-modal>

  <!-- Question Modal - Outside parent modal to prevent nesting issues -->
  <d-modal
    :active="showQuestionModal"
    :width="850"
    @close="closeQuestionModal"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <QuestionModal
      :question="selectedQuestion"
      :item="item"
      :content-listing="contentListing"
      @close="closeQuestionModal"
      @saved="handleQuestionSaved"
    />
  </d-modal>

  <!-- Delete Confirmation Modal -->
  <ConfirmModal
    :active="deleteModalActive"
    :title="'Delete Question'"
    :message="`Are you sure you want to delete this question?<br><br>This action cannot be undone.`"
    :confirm-text="'Delete Question'"
    @close="closeDeleteModal"
    @confirm="handleDeleteConfirmed"
  />
</template>

<script>
  import axios from 'axios';
  import { toast } from '@services/toast.service';
  import { exportQuizQuestions } from '@resources/scripts/services/quizQuestionsApi.service';
  import QuestionModal from './QuestionModal.vue';
  import ConfirmModal from '../../../Users/components/ConfirmModal.vue';

  export default {
    name: 'QuestionListModal',

    components: {
      QuestionModal,
      ConfirmModal,
    },

    props: {
      active: {
        type: Boolean,
        default: false,
      },
      item: {
        type: Object,
        default: () => ({}),
      },
      contentListing: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['close'],

    data() {
      return {
        questions: [],
        hoveredQuestionId: 0,
        showQuestionModal: false,
        selectedQuestion: null,
        deleteModalActive: false,
        questionToDelete: null,
        exporting: false,
      };
    },

    computed: {
      isActive() {
        return this.active;
      },
    },

    watch: {
      active: {
        handler(newVal) {
          if (newVal) {
            this.getQuestions();
          }
        },
        immediate: true,
      },
    },

    methods: {
      async getQuestions() {
        if (!this.item?.id) return;

        try {
          const { data } = await axios.get(`/api/content-listings/items/interactive/${this.item.id}/questions`, {
            params: {
              pageSize: 500
            }
          });
          this.questions = data;
        } catch (error) {
          console.error('Error loading questions:', error);
          toast.error('Failed to load questions');
          this.questions = [];
        }
      },

      getCorrectAnswers(question) {
        if (!question || !question.answerChoices || !Array.isArray(question.answerChoices)) {
          console.log('Question or answerChoices missing:', question);
          return 'No answers available';
        }
        
        const correctAnswers = question.answerChoices
          .filter(answer => answer.isCorrect)
          .map(answer => answer.content);
          
        return correctAnswers.length > 0 ? correctAnswers.join(', ') : 'No correct answer set';
      },

      launchQuestionModal(question = null) {
        this.selectedQuestion = question;
        this.showQuestionModal = true;
      },

      closeQuestionModal() {
        this.showQuestionModal = false;
        this.selectedQuestion = null;
      },

      handleQuestionSaved() {
        this.closeQuestionModal();
        this.getQuestions();
        toast.success('Question saved successfully');
      },

      launchDeletePrompt(question) {
        this.questionToDelete = question;
        this.deleteModalActive = true;
      },

      closeDeleteModal() {
        this.deleteModalActive = false;
        this.questionToDelete = null;
      },

      async handleDeleteConfirmed() {
        if (!this.questionToDelete) return;

        // Store the question ID locally to avoid null reference issues
        const questionId = this.questionToDelete.id;
        const questionToDelete = this.questionToDelete;

        try {
          const response = await axios.delete(`/api/content-listings/items/interactive/questions/${questionId}`);
          console.log('Delete response:', response);
          
          // Remove from local list using the stored ID
          this.questions = this.questions.filter(q => q.id !== questionId);
          toast.success('Question deleted successfully');
          this.closeDeleteModal();
        } catch (error) {
          console.error('Error deleting question:', error);
          
          // Check if this is an HTTP error or a JavaScript error
          if (error.response) {
            console.error('Error response:', error.response);
            console.error('Error status:', error.response.status);
            console.error('Error data:', error.response.data);
            
            // Check if it's actually a successful deletion with non-standard response
            if (error.response.status === 200 || error.response.status === 204 || error.response.status === 202) {
              // It was actually successful
              this.questions = this.questions.filter(q => q.id !== questionId);
              toast.success('Question deleted successfully');
              this.closeDeleteModal();
            } else {
              toast.error(`Failed to delete question: HTTP ${error.response.status}`);
            }
          } else {
            // This is a JavaScript error, not an HTTP error
            console.error('JavaScript error during delete:', error.message);
            toast.error(`Error during delete operation: ${error.message}`);
          }
        }
      },

      handleClose() {
        this.$emit('close');
      },

      async exportQuestions() {
        if (!this.item?.id) {
          toast.error('Unable to export questions: Missing item ID');
          return;
        }

        this.exporting = true;
        try {
          const response = await exportQuizQuestions(this.item.id);
          
          // Create a blob URL and trigger download
          const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Quiz-Questions-${this.item.id}-${new Date().toISOString().slice(0, 10)}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          toast.success('Quiz questions exported successfully');
        } catch (error) {
          console.error('Error exporting questions:', error);
          toast.error('Failed to export quiz questions');
        } finally {
          this.exporting = false;
        }
      },
    },
  };
</script>
